import React, { createRef, useEffect, useState } from 'react';
import c from 'classnames';
import i18next from 'i18next';
import PropTypes from 'prop-types';

import { Accordion } from '@uncinc/uncinc-react-kitchen-sink';

import useClickOutside from '../../hooks/useClickOutside';
import useEscape from '../../hooks/useEscape';
import AccordionMenuHeader from './Header';
import AccordionMenuItem from './MenuItem';

import './style.scss';

// TODO: https://uncinc.monday.com/boards/970563066/pulses/5903654737
import CustomAccordionItem from '../CustomAccordionItem';

const AccordionMenu = (props) => {
  const { items, className } = props;

  const ref = createRef();

  const [isCollapsed, setCollapsed] = useState(true);
  const [collapsedChildren, setCollapsedChildren] = useState(() => {
    if (!items || items.length === 0) {
      return;
    }

    return items.map((item) => ({ id: item.id, isCollapsed: true }));
  });

  const classNames = c('AccordionMenu', className);
  const iconClassNames = c('icon-variant-1', `icon-${isCollapsed ? 'caret' : 'cross'}`);

  /**
   * Function that sets the isCollapsed value for each child.
   * @param {string} childId
   * id of the child
   *
   * @param {boolean} newIsCollapsed
   * new value isCollapsed should be for current child.
   *
   * @param {boolean} forceCollapsingInactiveChildren
   * When true, all children not equal to the given id, will be forced to collapse,
   * leaving only the active child uncollapsed. Is false by default.
   *
   * @returns {void}
   */
  const handleCollapsedChildren = (childId, newIsCollapsed, forceCollapsingInactiveChildren = false) => {
    setCollapsedChildren((oldCollapsedChildren) => {
      if (!oldCollapsedChildren || oldCollapsedChildren.length === 0) {
        return;
      }

      return oldCollapsedChildren.map((child) => ({
        ...child,
        isCollapsed: child.id === childId ?
          newIsCollapsed :
          (forceCollapsingInactiveChildren || child.isCollapsed),
      }));
    });
  };

  useClickOutside({
    refs: [ref],
    unless: isCollapsed,
    onClick: () => setCollapsed(true),
  });

  useEscape({
    unless: isCollapsed,
    onPress: () => setCollapsed(true),
  });

  useEffect(() => {
    if (isCollapsed) {
      return;
    }

    const activeSections = document.querySelectorAll('.InlineMenu__nav-item.is-active > a');

    if (!activeSections || activeSections.length === 0) {
      return;
    }

    const latestActiveSection = activeSections[activeSections.length - 1];
    const latestActiveSectionId = latestActiveSection.href.slice(
      latestActiveSection.href.indexOf('#') + 1,
    );

    const activeChapter = items.find((chapter) => {
      const sectionIds = chapter.sections.map((section) => section.id);
      return sectionIds.includes(latestActiveSectionId);
    });

    handleCollapsedChildren(activeChapter.id, false, true);
  }, [isCollapsed, items]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Accordion className={classNames}>
      <CustomAccordionItem
        collapsed={isCollapsed}
        className="AccordionMenu__item"
        iconClassName={iconClassNames}
        onChange={(isCollapsedInternal) => {
          setCollapsed(isCollapsedInternal);
        }}
        title={!isCollapsed && i18next.t('button.close')}
        titleComponent={({ children }) => (
          <AccordionMenuHeader items={items}>
            {children}
          </AccordionMenuHeader>
        )}
        content={(
          <div className="AccordionMenu__content">
            <div className="AccordionMenu__content__wrapper" ref={ref}>
              <Accordion>
                <ul className="AccordionMenu__Menu">
                  {items.map((item) => (
                    <li key={item.id}>
                      <AccordionMenuItem
                        {...item}
                        isCollapsed={collapsedChildren.find((child) => child.id === item.id)?.isCollapsed}
                        onChange={(isCollapsedInternal) => {
                          handleCollapsedChildren(item.id, isCollapsedInternal);
                        }}
                        onClickMenuLink={() => {
                          setCollapsed(true);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </Accordion>
            </div>
          </div>
        )}
      />
    </Accordion>
  );
};

AccordionMenu.defaultProps = {
  className: '',
};

AccordionMenu.propTypes = {
  items: PropTypes.arrayOf(PropTypes.exact({
    id: PropTypes.string,
    number: PropTypes.number,
    title: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.exact({
      id: PropTypes.string,
      number: PropTypes.string,
      title: PropTypes.string,
    })),
  })).isRequired,
  className: PropTypes.string,
};

export default AccordionMenu;
